import { isUndefined, toPercentString } from '@aloft/toolkit-shared';
import { Block, Flex, Typography } from '@aloft/toolkit-sui';

import { LoadingScreen } from '../../../components/LoadingScreen/LoadingScreen.tsx';
import { NoData } from '../NoData.tsx';
import { usePublicRecordForListing } from '../hooks/usePublicRecordForListing.ts';
import { ReferenceViewID } from '../index.ts';
import { getAssessedLandToValueRatio } from '../utils/getAssessedLandToValueRatio.ts';
import { getMarketLandToValueRatio } from '../utils/getMarketLandToValueRatio.ts';
import { PublicRecordInfoColumn } from './PublicRecordInfoColumn.tsx';
import { PublicRecordInfoSection } from './PublicRecordInfoSection.tsx';
import { PublicRecordValueWithLabel } from './PublicRecordValueWithLabel.tsx';

export const Tkv2PublicRecordSheet = ({
  referenceViewID,
}: {
  referenceViewID: ReferenceViewID;
}) => {
  const publicRecord = usePublicRecordForListing();

  if (publicRecord.isLoading) return <LoadingScreen />;

  const publicRecordData = publicRecord.isError ? undefined : publicRecord.data?.listing;

  if (isUndefined(publicRecordData)) {
    return (
      <NoData text="No public record information available" referenceViewID={referenceViewID} />
    );
  }

  const { TaxAssessedValueImprovements, TaxAssessedValueLand } = publicRecordData;
  const LandToValueRatio = getAssessedLandToValueRatio(publicRecordData);
  const MarketLandToValueRatio = getMarketLandToValueRatio(publicRecordData);

  const addressLine1 = `${publicRecordData?.PropertyAddressFull}`;
  const addressLine2 = `${publicRecordData?.PropertyAddressCity} ${publicRecordData?.PropertyAddressState}, ${publicRecordData?.PropertyAddressZIP}`;
  const parcelNumberRaw = publicRecordData?.ParcelNumberRaw;

  return (
    <Flex flexDirection="column" marginLeft="1rem" marginRight="1rem" width="100%">
      <Typography variant="h3">
        {publicRecordData.SitusCounty ?? 'Unknown'} County
        {parcelNumberRaw !== null ? ` (APN: ${parcelNumberRaw})` : ''}
      </Typography>
      <Block>
        <Typography variant="h2">{addressLine1}</Typography>
        <Typography variant="h3" color="textTertiary">
          {addressLine2}
        </Typography>
      </Block>

      <Block marginTop="1rem">
        <Typography variant="h1">Property Information</Typography>
      </Block>
      <PublicRecordInfoSection title="Location Information">
        <PublicRecordInfoColumn>
          <PublicRecordValueWithLabel label="Parcel Number" value={parcelNumberRaw} />
          <PublicRecordValueWithLabel
            label="Property Address"
            value={`${addressLine1} ${addressLine2}`}
          />
          <PublicRecordValueWithLabel label="County" value={publicRecordData?.SitusCounty} />
          <PublicRecordValueWithLabel
            label="Neighborhood Code"
            value={publicRecordData?.NeighborhoodCode}
          />
          <PublicRecordValueWithLabel
            label="Minor Civil Division Name"
            value={publicRecordData?.MinorCivilDivisionName}
          />
        </PublicRecordInfoColumn>
      </PublicRecordInfoSection>
      <PublicRecordInfoSection title="Property Details">
        <PublicRecordInfoColumn>
          <PublicRecordValueWithLabel
            label="Legal Description"
            value={publicRecordData?.LegalDescription}
          />
          <PublicRecordValueWithLabel
            label="Property Type"
            value={publicRecordData?.PropertyUseGroup}
          />
          <PublicRecordValueWithLabel
            label="Year Built"
            value={publicRecordData?.YearBuilt?.toString()}
          />
          <PublicRecordValueWithLabel
            label="Effective Year"
            value={
              publicRecordData?.EffectiveYearBuilt === null ||
                publicRecordData?.EffectiveYearBuilt === -1
                ? '-'
                : String(publicRecordData.EffectiveYearBuilt)
            }
          />
        </PublicRecordInfoColumn>
      </PublicRecordInfoSection>
      <PublicRecordInfoSection title="Physical Property Attributes">
        <PublicRecordInfoColumn>
          <PublicRecordValueWithLabel
            label="Total Finished Area (AGLA)"
            value={
              publicRecordData?.AreaBuilding
                ? publicRecordData?.AreaBuilding?.toString()
                : publicRecordData?.AreaGross?.toString()
            }
          />
          <PublicRecordValueWithLabel
            label="Lot Size"
            value={publicRecordData?.AreaLotSF?.toString()}
          />
          <PublicRecordValueWithLabel
            label="Attic"
            value={
              publicRecordData?.RoomsAtticFlag ? publicRecordData?.RoomsAtticArea?.toString() : null
            }
          />
          <PublicRecordValueWithLabel
            label="Basement"
            value={publicRecordData?.RoomsBasementArea?.toString()}
          />
          <PublicRecordValueWithLabel
            label="Finished Basement"
            value={publicRecordData?.RoomsBasementAreaFinished?.toString()}
          />
          <PublicRecordValueWithLabel
            label="Total Number of Buildings"
            value={publicRecordData?.BuildingsCount}
          />
        </PublicRecordInfoColumn>
      </PublicRecordInfoSection>
      <PublicRecordInfoSection title="Parking and Outdoor Spaces">
        <PublicRecordInfoColumn>
          <PublicRecordValueWithLabel
            label="Parking Spaces"
            value={publicRecordData?.ParkingSpaceCount}
          />
          <PublicRecordValueWithLabel label="Garage" value={publicRecordData?.ParkingGarage} />
          {publicRecordData.ParkingGarage && (
            <PublicRecordValueWithLabel
              label="Garage Area"
              value={publicRecordData?.ParkingGarageArea?.toString()}
            />
          )}
          <PublicRecordValueWithLabel label="Carport" value={publicRecordData?.ParkingCarport} />
          {publicRecordData.ParkingCarport && (
            <PublicRecordValueWithLabel
              label="Carport Area"
              value={publicRecordData?.ParkingCarportArea?.toString()}
            />
          )}
          <PublicRecordValueWithLabel
            label="Driveway"
            value={publicRecordData?.DrivewayArea?.toString()}
          />
          <PublicRecordValueWithLabel
            label="Porch"
            value={publicRecordData?.PorchArea?.toString()}
          />
          <PublicRecordValueWithLabel
            label="Patio"
            value={publicRecordData?.PatioArea?.toString()}
          />
          <PublicRecordValueWithLabel label="Deck" value={publicRecordData?.DeckArea?.toString()} />
          <PublicRecordValueWithLabel label="Pool" value={publicRecordData?.PoolArea?.toString()} />
          {publicRecordData.ShedArea && (
            <PublicRecordValueWithLabel
              label="Shed"
              value={publicRecordData?.ShedArea?.toString()}
            />
          )}
          {publicRecordData.GuestHouseArea && (
            <PublicRecordValueWithLabel
              label="Guest House"
              value={publicRecordData?.GuestHouseArea?.toString()}
            />
          )}
        </PublicRecordInfoColumn>
      </PublicRecordInfoSection>
      <PublicRecordInfoSection title="General Property Characteristics">
        <PublicRecordInfoColumn>
          <PublicRecordValueWithLabel label="Rooms (Total)" value={publicRecordData?.RoomsCount} />
          <PublicRecordValueWithLabel
            label="Full Baths"
            value={
              publicRecordData?.BathCount && publicRecordData?.BathCount > 0
                ? publicRecordData.BathCount - (publicRecordData?.BathPartialCount ?? 0)
                : undefined
            }
          />
          <PublicRecordValueWithLabel
            label="Half Baths"
            value={publicRecordData?.BathPartialCount}
          />
          <PublicRecordValueWithLabel label="Bedrooms" value={publicRecordData?.BedroomsCount} />
          <PublicRecordValueWithLabel label="Stories" value={publicRecordData?.StoriesCount} />
          <PublicRecordValueWithLabel label="Units (Total)" value={publicRecordData?.UnitsCount} />
          <PublicRecordValueWithLabel label="Style" value={publicRecordData?.StructureStyle} />
        </PublicRecordInfoColumn>
      </PublicRecordInfoSection>
      <PublicRecordInfoSection title="Exterior and Roof Details">
        <PublicRecordInfoColumn>
          <PublicRecordValueWithLabel
            label="Exterior Wall"
            value={publicRecordData?.Exterior1Code}
          />
          <PublicRecordValueWithLabel
            label="Roof Construction"
            value={publicRecordData?.RoofMaterial}
          />
          <PublicRecordValueWithLabel label="View" value={publicRecordData?.ViewDescription} />
        </PublicRecordInfoColumn>
      </PublicRecordInfoSection>
      <PublicRecordInfoSection title="HVAC and Heating Systems">
        <PublicRecordInfoColumn>
          <PublicRecordValueWithLabel
            label="Cooling System"
            value={publicRecordData?.HVACCoolingDetail}
          />
          <PublicRecordValueWithLabel
            label="Heat System"
            value={publicRecordData?.HVACHeatingDetail}
          />
          <PublicRecordValueWithLabel
            label="Heat Source"
            value={publicRecordData?.HVACHeatingFuel}
          />
        </PublicRecordInfoColumn>
      </PublicRecordInfoSection>
      <PublicRecordInfoSection title="Utilities and Infrastructure">
        <PublicRecordInfoColumn>
          <PublicRecordValueWithLabel
            label="Sewer/Septic"
            value={publicRecordData?.UtilitiesSewageUsage}
          />
          <PublicRecordValueWithLabel
            label="Water"
            value={publicRecordData?.UtilitiesWaterSource}
          />
        </PublicRecordInfoColumn>
      </PublicRecordInfoSection>
      <PublicRecordInfoSection title="Construction Details">
        <PublicRecordInfoColumn>
          <PublicRecordValueWithLabel
            label="Construction Type"
            value={publicRecordData?.Construction}
          />
          <PublicRecordValueWithLabel label="Foundation" value={publicRecordData?.Foundation} />
        </PublicRecordInfoColumn>
      </PublicRecordInfoSection>
      <PublicRecordInfoSection title="Interior Fixtures and Finishes">
        <PublicRecordInfoColumn>
          <PublicRecordValueWithLabel
            label="Plumbing Fixtures (Total)"
            value={publicRecordData?.PlumbingFixturesCount}
          />
          <PublicRecordValueWithLabel
            label="Flooring"
            value={publicRecordData?.FlooringMaterialPrimary}
          />
        </PublicRecordInfoColumn>
      </PublicRecordInfoSection>

      <Block marginTop="1rem">
        <Typography variant="h1">Ownership and Tax Information</Typography>
      </Block>

      <PublicRecordInfoSection title="Owner Information">
        <PublicRecordInfoColumn>
          <PublicRecordValueWithLabel
            label="Owner Name"
            value={`${publicRecordData?.PartyOwner1NameFull ?? ''} ${publicRecordData?.PartyOwner2NameFull ?? ''
              } ${publicRecordData?.PartyOwner3NameFull ?? ''} ${publicRecordData?.PartyOwner4NameFull ?? ''
              }`}
          />
          <PublicRecordValueWithLabel
            label="Owner Address"
            value={`${publicRecordData?.ContactOwnerMailAddressFull ?? ''} ${publicRecordData?.ContactOwnerMailAddressCity ?? ''
              } ${publicRecordData?.ContactOwnerMailAddressState ?? ''} ${publicRecordData?.ContactOwnerMailAddressZIP ?? ''
              }`}
          />
          <PublicRecordValueWithLabel label="Company Owned" value={publicRecordData?.CompanyFlag} />
        </PublicRecordInfoColumn>
      </PublicRecordInfoSection>
      <PublicRecordInfoSection title="Assessment and Valuation Information">
        <PublicRecordInfoColumn>
          <PublicRecordValueWithLabel
            label="Assessment Year"
            value={publicRecordData?.TaxYearAssessed?.toString()}
          />
          <PublicRecordValueWithLabel
            label="Assessed Value (Improvements)"
            value={TaxAssessedValueImprovements}
          />
          <PublicRecordValueWithLabel label="Assessed Value (Land)" value={TaxAssessedValueLand} />
          <PublicRecordValueWithLabel
            label="Assessed Value (Total)"
            value={publicRecordData?.TaxAssessedValueTotal}
          />
          <PublicRecordValueWithLabel
            label="Assessed Land to Value %"
            value={LandToValueRatio !== null ? toPercentString(LandToValueRatio, 2) : '-'}
          />
          <PublicRecordValueWithLabel
            label="Market Value Year"
            value={publicRecordData?.TaxMarketValueYear?.toString()}
          />
          <PublicRecordValueWithLabel
            label="Market Value (Improvements)"
            value={publicRecordData?.TaxMarketValueImprovements}
          />
          <PublicRecordValueWithLabel
            label="Market Value (Land)"
            value={publicRecordData?.TaxMarketValueLand}
          />
          <PublicRecordValueWithLabel
            label="Market Value (Total)"
            value={publicRecordData?.TaxMarketValueTotal}
          />
          <PublicRecordValueWithLabel
            label="Market Land to Value %"
            value={
              MarketLandToValueRatio !== null ? toPercentString(MarketLandToValueRatio, 2) : '-'
            }
          />
        </PublicRecordInfoColumn>
      </PublicRecordInfoSection>
      <PublicRecordInfoSection title="Tax Information">
        <PublicRecordInfoColumn>
          <PublicRecordValueWithLabel
            label="Tax Year"
            value={publicRecordData?.TaxFiscalYear?.toString()}
          />
          <PublicRecordValueWithLabel label="Tax Area" value={publicRecordData?.TaxRateArea} />
          <PublicRecordValueWithLabel
            label="Tax Amount"
            value={publicRecordData?.TaxBilledAmount}
          />
        </PublicRecordInfoColumn>
      </PublicRecordInfoSection>
      <PublicRecordInfoSection title="Sales History">
        <PublicRecordInfoColumn>
          <PublicRecordValueWithLabel
            label="Last Sale Date"
            value={publicRecordData?.AssessorLastSaleDate?.format('MM/dd/yyyy')}
          />
          <PublicRecordValueWithLabel
            label="Last Sale Amount Paid (Owner)"
            value={publicRecordData?.AssessorLastSaleAmount}
          />
          <PublicRecordValueWithLabel
            label="Transaction ID"
            value={publicRecordData?.LastOwnershipTransferDocumentNumber}
          />
          <PublicRecordValueWithLabel
            label="Prior Sale Date"
            value={publicRecordData?.AssessorPriorSaleDate?.format('MM/dd/yyyy')}
          />
          <PublicRecordValueWithLabel
            label="Prior Sale Amount Paid (Owner)"
            value={publicRecordData?.AssessorPriorSaleAmount}
          />
        </PublicRecordInfoColumn>
      </PublicRecordInfoSection>
      <Block minHeight="2rem" />
    </Flex>
  );
};
